import type { PersistedClient } from '@tanstack/react-query-persist-client'

type QueryKey = readonly unknown[]

function isUserQueryAllowed(queryKey: QueryKey): boolean {
  const segment = queryKey[1]

  if (segment === 'profile') {
    return queryKey[2] === 'v2'
  }

  return (
    segment === 'customer-info' ||
    segment === 'deletion-eligibility' ||
    segment === 'email-verification'
  )
}

function isProgressQueryAllowed(queryKey: QueryKey): boolean {
  return queryKey[1] === 'activeWeek'
}

function isExerciseQueryAllowed(queryKey: QueryKey): boolean {
  return queryKey[1] === 'goal'
}

function isAuthFlowQueryAllowed(queryKey: QueryKey): boolean {
  return (
    queryKey[1] === 'pending-signup-first-name' ||
    queryKey[1] === 'create-user-attempt'
  )
}

function isContentfulQueryAllowed(queryKey: QueryKey): boolean {
  return queryKey[1] === 'translations' && typeof queryKey[2] === 'string'
}

export function shouldPersistQueryKey(queryKey: QueryKey): boolean {
  const root = queryKey[0]

  if (typeof root !== 'string') {
    return false
  }

  switch (root) {
    case 'app':
    case 'dashboard':
    case 'library':
    case 'sessions':
      return true
    case 'user':
      return isUserQueryAllowed(queryKey)
    case 'progress':
      return isProgressQueryAllowed(queryKey)
    case 'exercise':
      return isExerciseQueryAllowed(queryKey)
    case 'favorites':
      return queryKey.length === 1
    case 'auth-flow':
      return isAuthFlowQueryAllowed(queryKey)
    case 'contentful':
      return isContentfulQueryAllowed(queryKey)
    default:
      return false
  }
}

export function prunePersistedClient(
  persistedClient: PersistedClient,
): PersistedClient {
  return {
    ...persistedClient,
    clientState: {
      ...persistedClient.clientState,
      mutations: [],
      queries: persistedClient.clientState.queries.filter(
        (query) =>
          query.state.status === 'success' &&
          shouldPersistQueryKey(query.queryKey),
      ),
    },
  }
}

export function isStorageQuotaExceededError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') {
    return false
  }

  const name = 'name' in error ? error.name : undefined
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') {
    return true
  }

  const message = 'message' in error ? error.message : undefined
  return (
    typeof message === 'string' &&
    message.toLowerCase().includes('exceeded the quota')
  )
}
